import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Dialog, DialogTitle, IconButton } from '@mui/material';
import { handleFormErrors } from '../../../shared/utils/errors';
import { Close as CloseIcon } from '@mui/icons-material';
import { DatePickerDefault } from '../../../shared/components/inputs/DatePickerDefault';
import moment from 'moment';
import { InputDefault } from '../../../shared/components/inputs/InputDefault';
import { ButtonDefault } from '../../../shared/components/ButtonDefault';
import { SelectDefault } from '../../../shared/components/inputs/SelectDefault';
import {
  PaypalStatuses,
  Plan,
  usePlansQuery,
  User,
} from '../../../shared/types/generated';
import { createCustomSubscriptionSchema } from '../subscription-validator';
import { CreateCustomSubscriptionSchemaFormType } from '../subscription-types';
import { PAYMENT_METHODS_CUSTOM_ALLOWED, PROJECT_COLOR } from '../../../shared/constants';

interface CreateCouponModalProps {
  user: User;
  open: boolean;
  handleClose: () => void;
  loading?: boolean;
  onSubmit: (data: CreateCustomSubscriptionSchemaFormType) => void;
}

export const CreateCustomSubscriptionModal: React.FC<
  CreateCouponModalProps
> = ({ user, open, handleClose, loading = false, onSubmit }) => {
  const { data: plansData } = usePlansQuery({
    variables: {
      where: { status: PaypalStatuses.Active },
    },
  });
  const plans = useMemo(
    () => (plansData?.plans?.items as Plan[]) || [],
    [plansData],
  );

  const [planOptions, setPlanOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(createCustomSubscriptionSchema),
    defaultValues: {
      periodStartDate: new Date().toISOString(),
      periodEndDate: new Date().toISOString(),
      email: user.email,
      user: user.name,
      transactionId: '',
      plan: '',
    },
  });

  const onFormSubmit = handleSubmit(
    async (dataForm) => {
      onSubmit(dataForm);
    },
    (error) => {
      handleFormErrors(error);
    },
  );

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  useEffect(() => {
    if (plans?.length > 0) {
      setPlanOptions([
        ...plans.map((plan) => ({
          value: plan?.id?.toString() as string,
          label: plan.title as string,
        })),
      ]);
    }
  }, [plans]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose();
        reset();
      }}
      fullWidth
      maxWidth="md">
      <DialogTitle color="black" fontWeight={700}>
        Crear suscripción
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => {
          handleClose();
          reset();
        }}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}>
        <CloseIcon fontSize="small" />
      </IconButton>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={onFormSubmit}
        sx={{
          padding: 2,
          margin: '0 auto',
          px: 3,
        }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="user"
              control={control}
              render={({ field }) => (
                <InputDefault
                  {...field}
                  fullWidth
                  labelName="Usuario"
                  variant="outlined"
                  error={!!errors.user}
                  helperText={errors.user?.message}
                  readOnly
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <InputDefault
                  {...field}
                  fullWidth
                  labelName="Email"
                  variant="outlined"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  readOnly
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name={'periodStartDate'}
              control={control}
              render={({ field: { value, onChange } }) => (
                <DatePickerDefault
                  labelName="Fecha de inicio"
                  textFieldProps={{
                    helperText: errors.periodStartDate?.message,
                    error: !!errors.periodStartDate?.message,
                    placeholder: 'DD/MM/AAAA',
                  }}
                  value={value && value !== '' ? moment(value as string) : null}
                  onChange={onChange}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name={'periodEndDate'}
              control={control}
              render={({ field: { value, onChange } }) => (
                <DatePickerDefault
                  labelName="Fecha de fin"
                  textFieldProps={{
                    helperText: errors.periodEndDate?.message,
                    error: !!errors.periodEndDate?.message,
                    placeholder: 'DD/MM/AAAA',
                  }}
                  value={value && value !== '' ? moment(value as string) : null}
                  onChange={onChange}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="plan"
              control={control}
              render={({ field }) => (
                <SelectDefault
                  {...field}
                  fullWidth
                  labelName="Plan"
                  variant="outlined"
                  type="number"
                  placeholder="Selecciona un plan"
                  options={planOptions}
                  error={!!errors.plan}
                  helperText={errors.plan?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="paymentMethod"
              control={control}
              render={({ field }) => (
                <SelectDefault
                  {...field}
                  fullWidth
                  labelName="Método de pago"
                  variant="outlined"
                  options={PAYMENT_METHODS_CUSTOM_ALLOWED.map((method) => ({
                    value: method,
                    label: method,
                  }))}
                  error={!!errors.paymentMethod}
                  helperText={errors.paymentMethod?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="transactionId"
              control={control}
              render={({ field }) => (
                <InputDefault
                  {...field}
                  fullWidth
                  labelName="Id de la transacción"
                  variant="outlined"
                  placeholder="Id de la transacción"
                  error={!!errors.transactionId}
                  helperText={errors.transactionId?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <InputDefault
                  {...field}
                  fullWidth
                  labelName="Notas de la suscripción"
                  variant="outlined"
                  placeholder="Notas de la suscripción"
                  error={!!errors.notes}
                  helperText={errors.notes?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <ButtonDefault
              type="submit"
              fullWidth
              disabled={!isValid}
              isLoading={loading}
              style={{
                textTransform: 'inherit',
                fontWeight: 700,
                background: PROJECT_COLOR,
                ...(!isValid && { background: '#d3d3d3' }),
              }}>
              Crear suscripción
            </ButtonDefault>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};
